/* eslint-disable @typescript-eslint/no-require-imports, @typescript-eslint/no-var-requires */
const parseAddress = require('parse-address');
/* eslint-enable @typescript-eslint/no-require-imports, @typescript-eslint/no-var-requires */

/**
 * Parsed address structure compatible with Schema.org PostalAddress
 */
export interface ParsedAddress {
  streetAddress: string;
  addressLocality: string; // city
  addressRegion: string; // state
  postalCode: string;
  addressCountry: string;
  formatted: string;
}

/**
 * Raw result from parse-address library
 */
interface ParseAddressResult {
  number?: string; // street number
  prefix?: string; // street prefix (N, S, E, W)
  street?: string; // street name
  type?: string; // street type (St, Ave, Blvd, etc.)
  suffix?: string; // street suffix (N, S, E, W)
  city?: string;
  state?: string;
  zip?: string;
  plus4?: string; // ZIP+4 extension
  country?: string;
  sec_unit_type?: string; // secondary unit type (Apt, Suite, etc.)
  sec_unit_num?: string; // secondary unit number
}

/**
 * US state abbreviations for validation
 */
const US_STATES = new Set([
  'AL',
  'AK',
  'AZ',
  'AR',
  'CA',
  'CO',
  'CT',
  'DE',
  'FL',
  'GA',
  'HI',
  'ID',
  'IL',
  'IN',
  'IA',
  'KS',
  'KY',
  'LA',
  'ME',
  'MD',
  'MA',
  'MI',
  'MN',
  'MS',
  'MO',
  'MT',
  'NE',
  'NV',
  'NH',
  'NJ',
  'NM',
  'NY',
  'NC',
  'ND',
  'OH',
  'OK',
  'OR',
  'PA',
  'RI',
  'SC',
  'SD',
  'TN',
  'TX',
  'UT',
  'VT',
  'VA',
  'WA',
  'WV',
  'WI',
  'WY',
  'DC',
  'PR',
  'VI',
  'GU',
  'AS',
  'MP',
]);

/**
 * Build street address from parsed components
 */
function buildStreetAddress(parsed: ParseAddressResult): string {
  const parts: string[] = [];

  if (parsed.number) parts.push(parsed.number);
  if (parsed.prefix) parts.push(parsed.prefix);
  if (parsed.street) parts.push(parsed.street);
  if (parsed.type) parts.push(parsed.type);
  if (parsed.suffix) parts.push(parsed.suffix);

  // Add secondary unit if present (Apt 4, Suite 100, etc.)
  if (parsed.sec_unit_type) {
    parts.push(parsed.sec_unit_type);
    if (parsed.sec_unit_num) parts.push(parsed.sec_unit_num);
  }

  return parts.join(' ');
}

/**
 * Normalize country name to standard code
 */
function normalizeCountry(country?: string): string {
  if (!country) return 'US'; // Default to US since we're US-only

  const upper = country.toUpperCase().trim();

  const countryMap: Record<string, string> = {
    USA: 'US',
    'UNITED STATES': 'US',
    'UNITED STATES OF AMERICA': 'US',
    'U.S.A.': 'US',
    'U.S.': 'US',
    US: 'US',
  };

  return countryMap[upper] || upper;
}

/**
 * Try to extract city and state from formats like "City, ST" or "City, ST ZIP"
 * This handles cases where parse-address doesn't find a street
 */
function parseCityStateOnly(address: string): ParsedAddress | null {
  // Pattern: "City, ST" or "City, ST ZIP" or "City, ST ZIP, Country"
  const patterns = [
    // City, ST ZIP, Country
    /^([^,]+),\s*([A-Z]{2})\s+([\d-]+),?\s*(.*)$/i,
    // City, ST, Country (no ZIP)
    /^([^,]+),\s*([A-Z]{2}),\s*(.+)$/i,
    // City, ST ZIP (no country)
    /^([^,]+),\s*([A-Z]{2})\s+([\d-]+)$/i,
    // City, ST (just city and state)
    /^([^,]+),\s*([A-Z]{2})$/i,
  ];

  for (const pattern of patterns) {
    const match = address.match(pattern);
    if (match) {
      const city = match[1]?.trim() || '';
      const state = match[2]?.toUpperCase() || '';

      // Validate it's a real US state
      if (!US_STATES.has(state)) continue;

      let zip = '';
      let country = 'US';

      if (pattern === patterns[0]) {
        // City, ST ZIP, Country
        zip = match[3]?.trim() || '';
        country = normalizeCountry(match[4]);
      } else if (pattern === patterns[1]) {
        // City, ST, Country
        country = normalizeCountry(match[3]);
      } else if (pattern === patterns[2]) {
        // City, ST ZIP
        zip = match[3]?.trim() || '';
      }
      // For pattern[3] (City, ST), we keep defaults

      return {
        streetAddress: '',
        addressLocality: city,
        addressRegion: state,
        postalCode: zip,
        addressCountry: country,
        formatted: address,
      };
    }
  }

  return null;
}

/**
 * Parse a Google Places formatted address string into structured components.
 *
 * This function uses the parse-address library to handle various US address formats:
 * - Full addresses: "123 Main St, Seattle, WA 98101, USA"
 * - City/State/Zip: "Seattle, WA 98101"
 * - City/State only: "Seattle, WA"
 * - Partial addresses with missing components
 *
 * @param googlePlaceAddress - The formatted address string from Google Places API
 * @returns ParsedAddress object with all components (empty strings for missing parts)
 *
 * @example
 * const addr = getAddress("123 Main St, Seattle, WA 98101, USA");
 * if (addr.addressLocality) {
 *   console.log(`City: ${addr.addressLocality}`); // "Seattle"
 * }
 * if (addr.postalCode) {
 *   console.log(`ZIP: ${addr.postalCode}`); // "98101"
 * }
 */
export function getAddress(googlePlaceAddress: string): ParsedAddress {
  // Handle empty/invalid input
  if (!googlePlaceAddress || typeof googlePlaceAddress !== 'string') {
    return {
      streetAddress: '',
      addressLocality: '',
      addressRegion: '',
      postalCode: '',
      addressCountry: '',
      formatted: '',
    };
  }

  const trimmedAddress = googlePlaceAddress.trim();

  if (!trimmedAddress) {
    return {
      streetAddress: '',
      addressLocality: '',
      addressRegion: '',
      postalCode: '',
      addressCountry: '',
      formatted: '',
    };
  }

  // Try parse-address library first
  const parsed: ParseAddressResult = parseAddress.parseLocation(trimmedAddress);

  // If parse-address found a city or state, use its results
  if (parsed && (parsed.city || parsed.state)) {
    const postalCode = parsed.zip ? (parsed.plus4 ? `${parsed.zip}-${parsed.plus4}` : parsed.zip) : '';

    return {
      streetAddress: buildStreetAddress(parsed),
      addressLocality: parsed.city || '',
      addressRegion: parsed.state || '',
      postalCode,
      addressCountry: normalizeCountry(parsed.country),
      formatted: trimmedAddress,
    };
  }

  // Fallback: Try our custom city/state parser for simpler formats
  const cityStateResult = parseCityStateOnly(trimmedAddress);
  if (cityStateResult) {
    return cityStateResult;
  }

  // Ultimate fallback: return with just the formatted address
  // This handles edge cases where the address is unusual
  return {
    streetAddress: trimmedAddress,
    addressLocality: '',
    addressRegion: '',
    postalCode: '',
    addressCountry: 'US',
    formatted: trimmedAddress,
  };
}
